import { useSessionContext } from "@supabase/auth-helpers-react";
import { createContext, useRef, useEffect, useLayoutEffect, useContext, useCallback } from "react";
import {
    BROADCAST_EVENT,
    BroadcastEvents,
    BroadcastTypes,
    Doctor,
    TranscriptSectionStatus,
    ValidateType,
} from "@common/utils/types";
import {
    incrementRecordingCount,
    addRecordingSection,
    decrementRecordingCount,
    removeRecord,
    addDocument,
    removeDocument,
} from "@redux/patientTableRecordsSlice";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import { usePatientBroadcastRefresh } from "@/state/hooks/usePatientBroadcastRefresh";
import { useStore } from "react-redux";
import type { RootState } from "@/app/store";
import {
    addTranscriptSection,
    getPatientData,
    getPatientVisits,
    handleActivePatientBroadcast,
    handleVisitUpdated,
    removeTranscriptSections,
    setAssetAsSelected,
    updateOrAddTranscriptSection,
} from "@features/patient-view/state/redux/activePatientDataSlice";
import { REALTIME_SUBSCRIBE_STATES, RealtimeChannel } from "@supabase/supabase-js";
import {
    addOrUpdateTabAndAddDocument,
    addToAllDocuments,
    removeDocumentFromTab,
    removeFromAllDocuments,
    resetProgress,
    setProgress,
    updateDocumentSectionInTab,
} from "@features/patient-view/state/redux/activeDocumentsSlice";
import { createTabId } from "@/common/utils/helpers";
import { isHiddenDocumentForVisitView } from "@common/lib/supabaseClient";
import * as Sentry from "@sentry/react";
import { addSelectedDocument, removeSelectedDocument } from "../redux/documentSlice";
import { CALL_STATUS_QUERY_KEY, PHONE_NUMBER_VERIFICATION_STATUS_QUERY_KEY } from "../hooks/usePhoneCall";
import { useAccountContext } from "./AccountProvider";
import { useQueryClient } from "@tanstack/react-query";
import { applyDocumentUpdate } from "@/features/patient-view/state/redux/applyDocumentUpdate";

// Pre-completion statuses iOS and API-Server upserts use when notifying the webapp that
// a brand-new transcript_section exists. Terminal statuses are excluded so that a late
// completion broadcast for a deliberately-discarded section can't resurrect it.
const NEW_SECTION_STATUSES: ReadonlySet<string> = new Set([
    TranscriptSectionStatus.NotStarted,
    TranscriptSectionStatus.Uploaded,
    TranscriptSectionStatus.InProgress,
]);

export const RealTimeContextApi = createContext<{
    selectedDocumentsRef: React.MutableRefObject<string[]>;
    generateOnStopRef: React.MutableRefObject<boolean>;
    selectedDoctorRef: React.MutableRefObject<Doctor | null>;
    activeMonitoringDocumentIdRef: React.MutableRefObject<string | null>;
    setActiveMonitoringDocumentId: (id: string | null) => void;
    recordingMonitoringDocumentIdRef: React.MutableRefObject<string | null>;
    captureMonitoringDocumentIdForRecording: () => void;
    emitEvent: (eventType: BroadcastEvents, payload: any) => Promise<void>;
}>({
    selectedDoctorRef: { current: null },
    selectedDocumentsRef: { current: [] },
    generateOnStopRef: { current: false },
    activeMonitoringDocumentIdRef: { current: null },
    setActiveMonitoringDocumentId: () => {},
    recordingMonitoringDocumentIdRef: { current: null },
    captureMonitoringDocumentIdForRecording: () => {},
    emitEvent: async () => {},
});

export const RealTimeContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { session, supabaseClient: supabase, isLoading } = useSessionContext();
    const { setCommunicationsData, accountData } = useAccountContext();
    const queryClient = useQueryClient();
    // Resolve store via context (not the singleton import) so tests can inject their own store
    // and we avoid the circular-import hazard Redux docs warn about. `.getState()` is still
    // synchronously consistent post-dispatch — that's what the EN-610 guard below relies on.
    const store = useStore<RootState>();
    const patientData = useAppSelector(getPatientData);
    const visits = useAppSelector(getPatientVisits);
    const { selectedDocuments, selectedHistoryDocuments } = useAppSelector((state) => state.documents);
    const { selectedDoctor } = useAppSelector((state) => state.doctors);

    // EN-1051: the currently-resolved clinic. The server emits every realtime event on the clinic
    // account_id (see app/framework/repo/events.ts — every emitter passes an account id), so this,
    // not `session.user.id`, is what the client must subscribe to. The auth uid only coincides with
    // it for the owner of a legacy single-user account; it diverges for non-owner members and for
    // every impersonation session, which is exactly when broadcasts went missing.
    //
    // Use `channelAccountId` below — not this value — as the actual channel identity; see why there.
    const clinicAccountId =
        accountData && typeof accountData === "object" && "id" in accountData && accountData.id
            ? String(accountData.id)
            : undefined;

    // EN-1051: `clinicAccountId` alone is not safe to use as the channel identity, because it goes
    // *backwards* on failure. `fetchAccountDataViaResolvedApi` swallows errors and returns null, and
    // AccountProvider then does `setAccountData({})` — so a single failed mid-session refetch flips
    // this to undefined while `dataLoaded` stays true and the UI keeps rendering. Keying the channel
    // straight off it would tear realtime down on that blip and not restore it until the next token
    // refresh. Before this ticket a failed refetch could not touch realtime at all, so that would be
    // a regression we introduced.
    //
    // So latch the last id we actually resolved and fall back to it: a defined -> undefined
    // transition keeps the existing channel, while a genuine clinic switch (A -> B) still changes the
    // identity and tears the old channel down. The latch is cleared on sign-out so the channel
    // identity really does go undefined there (which is what makes the effect below re-run and its
    // cleanup remove the channel) and so a subsequent different user cannot inherit it.
    //
    // The latch is maintained in an effect rather than written inline during render. A render that
    // is started and then discarded (StrictMode's double invoke, or any future concurrent feature)
    // still leaves an inline ref mutation behind, which would corrupt the latch with a value from a
    // render that was never committed. Committing it in an effect is safe *and* behaviour-preserving
    // here, because the latch is only ever READ on a render where `clinicAccountId` is falsy — and
    // on that render the effect from the previous commit has already stored the value. On the render
    // where `clinicAccountId` is truthy the `??` short-circuits before the ref is consulted at all.
    const isSignedIn = Boolean(session?.user?.id);
    const lastResolvedClinicAccountIdRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!isSignedIn) {
            lastResolvedClinicAccountIdRef.current = undefined;
        } else if (clinicAccountId) {
            lastResolvedClinicAccountIdRef.current = clinicAccountId;
        }
    }, [isSignedIn, clinicAccountId]);
    const channelAccountId = isSignedIn ? (clinicAccountId ?? lastResolvedClinicAccountIdRef.current) : undefined;

    // EN-1216 PR review: dedupes the handleActivePatientBroadcast refetch handleTranscriptionEvent
    // triggers on an accountId mismatch, so a repeatedly-misbehaving client (e.g. stuck retrying mid
    // account-switch) re-emitting mismatched broadcasts can't cause a refetch storm.
    const mismatchRefetchInFlightRef = useRef(false);
    // PR review finding: the in-flight ref alone only blocks OVERLAPPING refetches -- it resets
    // the instant one completes, so a client re-emitting a mismatched broadcast slower than a
    // single fetchPatientRecord round-trip (a "slow drip") could still force a fresh refetch on
    // every broadcast indefinitely. handleActivePatientBroadcast's own patientData.id
    // re-verification means this can't corrupt state, but it's still an easy amplification vector
    // (one small broadcast forcing a full patient refetch) for a malicious or stuck client on the
    // account channel. A cooldown bounds the worst case regardless of round-trip time.
    const mismatchRefetchLastAtRef = useRef(0);
    const MISMATCH_REFETCH_COOLDOWN_MS = 10_000;

    // EN-1051 (final review, C1): the clinic channel is necessary but NOT sufficient. Transcribe
    // broadcasts transcript-section status on whatever id the client sent as `req.userId`
    // (`SendEvent(ctx, req.userId, ...)` in Transcribe/app/usecase/{upload,transcribe}.ts). Since
    // EN-1266 the web sends the upload's storage account there: the clinic id for new non-owner
    // uploads, but the auth uid for owners and for legacy sections whose `account_id` is a uid.
    // `/transcription/start` carries no account header, and there is no polling fallback for
    // transcript-section status. Moving off the uid channel alone would therefore have taken live
    // recording status away from exactly the users this ticket is about (active non-owner members of
    // multi-user accounts).
    //
    // So hold BOTH channels. API-Server emits on the clinic account id and Transcribe on its
    // `req.userId`. When a member's request found the audio in the clinic folder, Transcribe also
    // emits on the clinic id so other members see it, so a member holding both channels can get
    // that event twice. That is harmless: the transcription handlers are update-by-id (no push),
    // so a repeat is a no-op. The one peer-to-peer event (`documentSelection`) is filtered by
    // `senderUserId` regardless of which channel carried it, and a user's own uid is not a
    // cross-tenant surface. Holding both also removes the cross-repo same-deploy constraint: an old
    // tab, a rolled-back Transcribe, or a rolled-back API-Server all keep working.
    //
    // `undefined` when the two ids are equal (the legacy single-user owner, where account.id ==
    // auth.uid()) so that case still holds exactly one channel.
    const userChannelId = isSignedIn && session?.user?.id !== channelAccountId ? session?.user?.id : undefined;

    // realtime channel refs and variables
    const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
    const emitEventRetryTimersRef = useRef<Set<NodeJS.Timeout>>(new Set());
    const isRetryingRef = useRef(false);
    const reconnectAttemptsRef = useRef(0);
    const subscribeToChannelRef = useRef<(() => Promise<void>) | null>(null);
    const isSettingUpChannelRef = useRef<boolean>(false);
    const currentChannelIdRef = useRef<string | undefined>(undefined);
    const maxReconnectAttempts = 5;
    const RECONNECT_DELAY = 5000;

    // get the current visit and check if it is a history visit
    const currentVisit = visits.find((visit) => visit.id === patientData?.id);
    const selectedDocumentIds = currentVisit?.isHistory ? selectedHistoryDocuments : selectedDocuments;

    // refs to store selected documents & whether transcribe call should generate them
    const selectedDocumentsRef = useRef<string[]>([]);
    const generateOnStopRef = useRef<boolean>(false);

    // the chat_completions id of a Monitoring document the user currently has open;
    // null for every other document type or when none is open
    const activeMonitoringDocumentIdRef = useRef<string | null>(null);
    const setActiveMonitoringDocumentId = useCallback((id: string | null) => {
        activeMonitoringDocumentIdRef.current = id;
    }, []);

    // Frozen at the moment recording actually STARTS (see AssetLibraryProvider.handleStartRecording),
    // unlike activeMonitoringDocumentIdRef above, which is a live view of "whichever document tab is
    // open right now" and keeps changing for as long as the recording widget stays open across tab
    // navigation. Without this separate snapshot, a user who opens a Monitoring document, starts
    // recording, then switches to a different tab mid-recording would have the clip attributed to
    // whatever tab was open when they stopped — not the Monitoring document that was open when they
    // started. useFileUpload reads this ref, not the live one, when it snapshots the upload target.
    const recordingMonitoringDocumentIdRef = useRef<string | null>(null);
    const captureMonitoringDocumentIdForRecording = useCallback(() => {
        recordingMonitoringDocumentIdRef.current = activeMonitoringDocumentIdRef.current;
    }, []);

    const selectedDoctorRef = useRef<Doctor | null>(selectedDoctor);
    const supaSuccessChannelRef = useRef<RealtimeChannel | null>(null);
    // The uid-named companion channel (see `userChannelId`). Receive-only: `emitEvent` always sends
    // on the clinic channel, because the clinic is who the peers are.
    const userChannelRef = useRef<RealtimeChannel | null>(null);

    // Update refs when relevant state changes
    useEffect(() => {
        selectedDocumentsRef.current = selectedDocumentIds;
        selectedDoctorRef.current = selectedDoctor;
    }, [selectedDocumentIds, selectedDoctor]);

    const dispatch = useAppDispatch();

    // emit a generic event on the supa success channel
    const emitEvent = useCallback(
        async (eventType: BroadcastEvents, payload: any, retryCount = 0) => {
            const {
                data: { session },
                error: sessionError,
            } = await supabase.auth.getSession();

            if (sessionError || !session) {
                Sentry.captureException(sessionError);
                return;
            }

            if (!session?.user?.id) {
                Sentry.captureMessage("Error in emitEvent in RealtimeProvider.tsx", {
                    level: "error",
                    extra: { eventType, payload },
                });
                return;
            }

            let channel = supaSuccessChannelRef.current;

            // If no channel exists, try to trigger channel creation
            // This handles cases where emitEvent is called before the channel is set up.
            // Note: being signed in is already guaranteed by the guard above, so this is only a
            // "do we have a channel" check — it is NOT an identity comparison. (The `session` in
            // scope here is the shadowed local from getSession(), not the provider's.)
            if (!channel) {
                // Try to trigger channel creation if subscribeToChannel is available
                if (subscribeToChannelRef.current) {
                    // Await channel creation to ensure the ref is set before checking
                    await subscribeToChannelRef.current();
                    // Check again after creation attempt (subscribeToChannel sets the ref)
                    channel = supaSuccessChannelRef.current;
                }
            }

            // Check if channel exists and is ready
            // httpSend works via REST API, so we don't need the channel to be fully subscribed
            // but we do need the channel object to exist
            if (!channel) {
                // For non-critical events, retry with exponential backoff
                const isNonCriticalEvent = [BroadcastEvents.documentSelection, BroadcastEvents.heartbeat].includes(
                    eventType,
                );

                if (isNonCriticalEvent && retryCount < 2) {
                    // Exponential backoff: 300ms, 800ms
                    const delay = [300, 800][retryCount];
                    const timeoutId = setTimeout(() => {
                        emitEventRetryTimersRef.current.delete(timeoutId);
                        emitEvent(eventType, payload, retryCount + 1);
                    }, delay);
                    emitEventRetryTimersRef.current.add(timeoutId);
                    return;
                }

                // Log as warning for non-critical events, error for critical ones
                // Only log if we've exhausted retries to reduce noise
                const logLevel = isNonCriticalEvent ? "warning" : "error";
                if (retryCount >= 2 || !isNonCriticalEvent) {
                    Sentry.captureMessage("Emit event - no channel after retries", {
                        level: logLevel,
                        extra: {
                            userId: session?.user?.id,
                            event: eventType,
                            payload: payload,
                            retryCount,
                        },
                    });
                }
                return;
            }

            // Channel exists, but check if it's in a bad state
            // httpSend should work even if channel is joining/leaving, but not if closed/errored
            if (channel.state === "closed" || channel.state === "errored") {
                const isNonCriticalEvent = [BroadcastEvents.documentSelection, BroadcastEvents.heartbeat].includes(
                    eventType,
                );

                if (isNonCriticalEvent && retryCount < 2) {
                    // Try to reconnect if channel is in bad state
                    if (subscribeToChannelRef.current) {
                        // Await channel recreation to ensure the ref is updated
                        await subscribeToChannelRef.current();
                        // Check if channel is now ready before retrying
                        channel = supaSuccessChannelRef.current;
                        if (channel && channel.state !== "closed" && channel.state !== "errored") {
                            // Channel is ready, continue with sending the event
                            // (will fall through to the httpSend call below)
                        } else {
                            // Channel still not ready, retry after a delay
                            const timeoutId = setTimeout(() => {
                                emitEventRetryTimersRef.current.delete(timeoutId);
                                emitEvent(eventType, payload, retryCount + 1);
                            }, 1000);
                            emitEventRetryTimersRef.current.add(timeoutId);
                            return;
                        }
                    } else {
                        // No subscribe function available, retry after a delay
                        const timeoutId = setTimeout(() => {
                            emitEventRetryTimersRef.current.delete(timeoutId);
                            emitEvent(eventType, payload, retryCount + 1);
                        }, 1000);
                        emitEventRetryTimersRef.current.add(timeoutId);
                        return;
                    }
                } else {
                    // Not a non-critical event or retries exhausted, log and return
                    const logLevel = isNonCriticalEvent ? "warning" : "error";
                    Sentry.captureMessage("Emit event - channel in bad state", {
                        level: logLevel,
                        extra: {
                            userId: session?.user?.id,
                            event: eventType,
                            payload: payload,
                            channelState: channel.state,
                            retryCount,
                        },
                    });
                    return;
                }
            }

            try {
                // EN-1051: stamp the emitting human's auth uid on every client-emitted event.
                // The channel is now the whole clinic, so a peer-to-peer event needs to carry who
                // sent it — `handleDocumentSelectionEvent` uses this to drop other members' clicks.
                // Stamped centrally rather than at the four call sites so it cannot be forgotten by
                // a future emitter. Server-emitted events never carry it, and no handler for a
                // server event reads it.
                const result = await channel.httpSend("client-update", {
                    type: "success",
                    eventType,
                    data: { ...payload, senderUserId: session.user.id },
                });
                if (!result.success) {
                    Sentry.captureMessage("Failed to send broadcast event", {
                        level: "error",
                        extra: {
                            eventType,
                            status: result.status,
                            error: result.error,
                            userId: session?.user?.id,
                        },
                    });
                }
                return;
            } catch (error) {
                Sentry.captureException(error);
                return;
            }
        },
        [supabase],
    );

    /*
     * This callback handles the subscription change events from the realtime channel
     * It handles the subscription status and reconnects if needed
     */
    const handleSubscriptionChange = useCallback(
        (status: `${REALTIME_SUBSCRIBE_STATES}`) => {
            const userId = session?.user?.id;

            if (reconnectTimerRef.current && !isRetryingRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }

            switch (status) {
                case "SUBSCRIBED":
                    isRetryingRef.current = false;
                    reconnectAttemptsRef.current = 0; // Reset attempts on successful connection
                    if (reconnectTimerRef.current) {
                        clearTimeout(reconnectTimerRef.current);
                        reconnectTimerRef.current = null;
                    }
                    break;

                case "TIMED_OUT":
                case "CHANNEL_ERROR":
                    Sentry.addBreadcrumb({
                        category: "realtime",
                        message: `${status} on channel`,
                        level: "error",
                        data: { userId: userId },
                    });

                    // Circuit breaker: stop trying after max attempts
                    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
                        Sentry.captureMessage("Max reconnection attempts reached", {
                            level: "error",
                            extra: {
                                userId: userId,
                                attempts: reconnectAttemptsRef.current,
                                status,
                            },
                        });
                        isRetryingRef.current = false;
                        reconnectAttemptsRef.current = 0;
                        return;
                    }

                    if (!isRetryingRef.current) {
                        reconnectAttemptsRef.current++;
                        const exponentialDelay = RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1);

                        Sentry.addBreadcrumb({
                            category: "realtime",
                            message: "Scheduling reconnect attempt",
                            level: "info",
                            data: {
                                userId: userId,
                                delay: exponentialDelay,
                                attempt: reconnectAttemptsRef.current,
                            },
                        });

                        isRetryingRef.current = true;
                        reconnectTimerRef.current = setTimeout(() => {
                            subscribeToChannelRef.current?.(); // try reconnecting
                            isRetryingRef.current = false;
                        }, exponentialDelay);
                    }
                    break;

                case "CLOSED":
                    Sentry.addBreadcrumb({
                        message: "Realtime channel closed",
                        level: "warning",
                        data: { userId: userId, status },
                    });
                    isRetryingRef.current = false;
                    if (reconnectTimerRef.current) {
                        clearTimeout(reconnectTimerRef.current);
                        reconnectTimerRef.current = null;
                    }

                    // Only reconnect on CLOSED if we haven't exceeded max attempts
                    if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                        reconnectAttemptsRef.current++;
                        subscribeToChannelRef.current?.();
                    }
                    break;

                default:
                    break;
            }
        },
        [session?.user?.id],
    );

    /*
     * This useEffect manages cleanup and reconnection when dependencies change
     * Initial channel setup is handled by the useEffect that sets subscribeToChannelRef
     */
    useEffect(() => {
        if (!supabase || !channelAccountId || isLoading) {
            return;
        }

        // Track the channel identity so the setup effect below can tell whether the channel it
        // finds belongs to the currently-resolved clinic.
        //
        // There is deliberately no teardown of the previous channel here. A teardown branch used to
        // sit at this spot, comparing the previous identity to the current one — it was dead code:
        // this effect's own cleanup (see the return below) has already run and already nulled
        // `supaSuccessChannelRef` by the time the body re-executes, so the branch could never see a
        // channel to remove. That cleanup IS the teardown path for a clinic switch, a sign-out, and
        // unmount alike. Removed rather than left in place with a comment claiming otherwise.
        currentChannelIdRef.current = channelAccountId;

        // Only reconnect if channel doesn't exist or is in a bad state
        // This prevents double calls on initial mount (which is handled by the other useEffect)
        // The ref is updated synchronously via useLayoutEffect, so it should always be current
        const currentChannel = supaSuccessChannelRef.current;
        if (
            (!currentChannel || currentChannel.state === "closed" || currentChannel.state === "errored") &&
            !isSettingUpChannelRef.current &&
            subscribeToChannelRef.current
        ) {
            isSettingUpChannelRef.current = true;
            // The ref is updated synchronously via useLayoutEffect before this effect runs,
            // so it should always have the latest function with the current clinic account id
            subscribeToChannelRef.current().finally(() => {
                isSettingUpChannelRef.current = false;
            });
        }

        // Always return cleanup function to ensure channel is cleaned up on unmount
        return () => {
            isRetryingRef.current = false;
            reconnectAttemptsRef.current = 0; // Reset attempts on unmount
            isSettingUpChannelRef.current = false;
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            // Clear all emitEvent retry timers to prevent resource leaks
            emitEventRetryTimersRef.current.forEach((timeoutId) => {
                clearTimeout(timeoutId);
            });
            emitEventRetryTimersRef.current.clear();
            if (supaSuccessChannelRef.current) {
                supabase.removeChannel(supaSuccessChannelRef.current).catch((e) => {
                    Sentry.captureException(e);
                });
                supaSuccessChannelRef.current = null;
            }
            // EN-1051: the uid companion channel is torn down with the clinic one, always. This is
            // the single teardown path for a clinic switch, a uid change, sign-out and unmount
            // alike, so the two channels cannot outlive each other.
            if (userChannelRef.current) {
                supabase.removeChannel(userChannelRef.current).catch((e) => {
                    Sentry.captureException(e);
                });
                userChannelRef.current = null;
            }
        };
    }, [supabase, channelAccountId, userChannelId, isLoading]);

    // heartbeat effect to keep the channel open
    useEffect(() => {
        const HEARTBEAT_INTERVAL = 60_000 * 20; // every 20 minutes
        const heartbeatInterval = setInterval(() => {
            emitEvent(BroadcastEvents.heartbeat, { timestamp: Date.now() });
        }, HEARTBEAT_INTERVAL);

        return () => clearInterval(heartbeatInterval);
    }, [emitEvent]);

    // Event handler functions
    const handleTranscriptionEvent = useCallback(
        (isSuccess: boolean, data: any) => {
            const {
                transcriptionId,
                transcriptSectionId,
                data: transcript,
                patientRecordId,
                status,
                audioDuration,
                unprocessed_filename,
                archived,
                // EN-1216: the clinic account this section belongs to (API-Server PR
                // happy-doc/API-Server#1732). Without it, the cross-client new-section row built
                // below has no account_id, so the receiving client's playback/move falls back to
                // its own session id and requests a Storage path that doesn't exist.
                accountId,
                // EN-1544: set by API-Server for a server-created section (a Call Capture call)
                origin,
                created_at: createdAt,
            } = data;

            // Archive broadcast from another client (iOS, another browser tab, Spork admin).
            // API-Server's archiveTranscriptSections emits this with `archived: true` after the
            // discard write succeeds. Same-tab discards already update the UI optimistically;
            // without this branch, a cross-client discard leaves a ghost row in the Library and
            // a stale section in the visit-list row's count until a manual refresh.
            //
            // Handle before incrementRecordingCount so the visit-list row's section list
            // shrinks (decrement) instead of being flipped to a completion status it never reached.
            if (archived) {
                // Note: decrementRecordingCount takes sectionIds (plural array) by design — it filters a set.
                // incrementRecordingCount takes sectionId (singular) because it maps one section's status.
                // Reducers are id-safe (find-by-id and filter-by-id-set both no-op on malformed ids),
                // so no explicit guard is needed — but log a breadcrumb so cross-client archives are
                // visible in Sentry, matching handleDocumentEvent's archived branch.
                Sentry.addBreadcrumb({
                    category: "realtime",
                    message: "Cross-client transcript section archive",
                    data: { transcriptSectionId, transcriptionId, patientRecordId },
                    level: "info",
                });
                dispatch(decrementRecordingCount({ transcriptionId, sectionIds: [transcriptSectionId] }));
                dispatch(removeTranscriptSections([transcriptSectionId]));
                return;
            }

            // patientTableRecordsSlice update — must run regardless of which view the user is on.
            // This slice powers the visit-list table and is independent of activePatientData.
            //
            // Gate on terminal status: cross-client new-section broadcasts (added in the same
            // PR as the archived branch above) reach this handler at non-terminal statuses
            // (NotStarted/Uploaded/InProgress). The dispatch below hard-codes a terminal status
            // from isSuccess, so firing it for a non-terminal broadcast would flip an existing
            // visit-list row's section to Completed/Failed even though it's still processing.
            // Skipping non-terminal broadcasts is safe — the cross-client new-section path
            // updates activePatientData only; patientTableRecordsSlice gets the terminal status
            // from the eventual completion broadcast (or a fetch on view switch).
            //
            // Safe to fire before the resurrection guard below: incrementRecordingCount is
            // update-only (`.map` over existing sections, no push — see slice JSDoc), so a late
            // completion for a section that was already removed from patientTableRecordsSlice
            // will no-op. Local discard callers (ThreeDotRecordingMenu, MoveRecordingModal,
            // TranscriptionModal) dispatch decrementRecordingCount alongside removeTranscriptSections,
            // keeping both slices in sync — any future caller that removes from activePatientData
            // MUST also dispatch decrementRecordingCount or a stale completion will flip a
            // still-present table row's status. See transcriptSectionResurrection.spec.ts for the
            // reducer invariants this handler depends on (handler-level tests are codebase-wide
            // debt; we follow the stopButtonOptimisticCancel.spec.ts precedent).
            if (!NEW_SECTION_STATUSES.has(status)) {
                dispatch(
                    incrementRecordingCount({
                        transcriptionId,
                        sectionId: transcriptSectionId,
                        status: isSuccess
                            ? TranscriptSectionStatus.Completed
                            : TranscriptSectionStatus.FailedErrorProcessing,
                    }),
                );
            }

            // Active-patient resurrection guard: if the section isn't in current activePatientData
            // (user discarded it OR navigated away), skip the Redux merge + auto-select.
            //
            // Read from store.getState() instead of a ref. Refs sync via useEffect (post-commit),
            // so a dispatch immediately followed by a realtime event leaves the ref stale and
            // drops a legitimate completion. The store snapshot is synchronously consistent
            // post-dispatch. `store` comes from `useStore()` (context), not a singleton import.
            //
            // DO NOT replace these `liveState.activePatientData.*` reads with the closure-scoped
            // `patientData` (from `useAppSelector(getPatientData)` at the top of this provider) —
            // realtime events fire between renders, so the selector value can be one render
            // behind the store. Using the selector here reintroduces the stale-snapshot bug.
            const liveState = store.getState();
            const liveSections = liveState.activePatientData.transcriptSections;
            const liveActivePatientId = liveState.activePatientData.patientData?.id;
            const liveDiscardedIds = liveState.activePatientData.discardedSectionIds;
            const sectionExists = liveSections.some((section) => section.id === transcriptSectionId);
            const isActivePatient = !!liveActivePatientId && liveActivePatientId === patientRecordId;
            if (!sectionExists) {
                // Cross-client new section: another client (iOS, second tab) just created a section
                // we haven't seen locally yet. API-Server's upsertTranscriptSection broadcasts at a
                // non-terminal status (not_started / uploaded / in_progress) explicitly so the
                // webapp can render the new clip as "Processing…" without waiting for refresh or
                // for Transcribe's terminal broadcast to land.
                //
                // Only accept non-terminal broadcasts here. Terminal broadcasts (completed,
                // failed_*, cancelled) for an unknown section are still suppressed — that's the
                // EN-610 resurrection guard for sections the user just discarded.
                if (isActivePatient && !archived && NEW_SECTION_STATUSES.has(status)) {
                    // Tombstone guard: a non-terminal broadcast can be either (a) a genuinely new
                    // section from another client OR (b) a delayed Uploaded/InProgress that was
                    // in flight when the user discarded the section locally (races the archive
                    // broadcast). Without this check, (b) would resurrect the just-discarded
                    // section. `removeTranscriptSections` populates `discardedSectionIds`, scoped
                    // to the active patient session and cleared on patient switch.
                    if (liveDiscardedIds.includes(transcriptSectionId)) {
                        Sentry.addBreadcrumb({
                            category: "realtime",
                            message: "Skipping cross-client new section — tombstoned (recently discarded)",
                            data: { transcriptSectionId, transcriptionId, patientRecordId, status },
                            level: "info",
                        });
                        return;
                    }
                    // PR review finding: accountId is client-supplied broadcast data (relayed from
                    // the server, not re-validated) and drives Storage path construction
                    // downstream. Trust it only when it agrees with channelAccountId -- the value
                    // this client's subscription is actually bound to (see EN-1051 above for why
                    // that, not clinicAccountId directly, is the authoritative "which account's
                    // broadcasts legitimately reach us" reference). Absence (a pre-happy-doc/
                    // API-Server#1732 backend, mid-deploy) is the expected/silent case; a genuine
                    // mismatch is reported so a malformed or stale broadcast can't silently steer
                    // a later Storage lookup into the wrong account's folder.
                    //
                    // Scoped inside isActivePatient/!sectionExists/non-terminal, not at the top of
                    // the handler: this channel carries every transcription broadcast for the
                    // whole clinic account, most of which are for a DIFFERENT patient than the one
                    // open here or are routine status updates on a section we already have. The
                    // refetch below must never fire for those — dispatching a refetch for whatever
                    // patientRecordId an unrelated broadcast happened to name would swap the open
                    // patient view to a different patient's data.
                    const resolvedAccountId = accountId === channelAccountId ? accountId : channelAccountId;
                    // PR review finding: captured here (not inlined below) so the post-refetch
                    // re-assertion further down dispatches the exact same payload as the immediate
                    // optimistic add.
                    const newSectionPayload = {
                        transcriptId: transcriptionId,
                        transcriptSection: {
                            id: transcriptSectionId,
                            transcription_id: transcriptionId,
                            data: transcript ?? "",
                            status,
                            audio_duration: audioDuration,
                            unprocessed_filename,
                            account_id: resolvedAccountId,
                            // only when sent, so a later broadcast without it never clears it
                            ...(origin ? { origin } : {}),
                            // the server's stamp when sent; otherwise the reducer falls back to this device's clock
                            ...(createdAt ? { created_at: createdAt } : {}),
                        },
                    };
                    if (accountId && accountId !== channelAccountId) {
                        Sentry.captureMessage("Realtime transcription broadcast accountId mismatch", {
                            level: "warning",
                            extra: {
                                broadcastAccountId: accountId,
                                channelAccountId,
                                transcriptSectionId,
                                transcriptionId,
                            },
                        });
                        // Sentry telemetry alone has no in-session recovery path if
                        // channelAccountId is ever the one that's stale (e.g. mid account-switch).
                        // Trigger a real refetch of the (confirmed-active) patient so the section
                        // self-corrects from the DB (which reads account_id correctly via
                        // PATIENT_RECORD_SELECT). Deduped via the in-flight ref: a
                        // repeatedly-misbehaving client re-emitting a mismatched broadcast (e.g.
                        // stuck retrying mid account-switch) must not trigger a refetch storm.
                        //
                        // PR review finding: this is a same-patient refresh, not a patient switch —
                        // must NOT use fetchPatientData. That thunk sets status=Loading (unmounts
                        // the view to a spinner) and its setPatientData reducer unconditionally
                        // clears discardedSectionIds on the "patient switch boundary" (see that
                        // reducer's comment), which would wipe this active session's tombstones and
                        // reopen the exact discard-resurrection race removeTranscriptSections guards
                        // against — plus it writes patientData with no check that the user hasn't
                        // navigated away while the fetch was in flight. handleActivePatientBroadcast
                        // is the existing same-patient-refresh thunk (already used below for the
                        // "patient" broadcast type): it re-verifies patientData.id === patientRecordId
                        // before writing, doesn't touch discardedSectionIds, and doesn't set Loading.
                        // PR review finding: the in-flight check alone only blocks overlapping
                        // refetches -- add a cooldown so a client re-emitting mismatches slower
                        // than one round-trip can't still force a refetch on every broadcast.
                        const now = Date.now();
                        if (
                            !mismatchRefetchInFlightRef.current &&
                            now - mismatchRefetchLastAtRef.current >= MISMATCH_REFETCH_COOLDOWN_MS
                        ) {
                            mismatchRefetchInFlightRef.current = true;
                            mismatchRefetchLastAtRef.current = now;
                            dispatch(handleActivePatientBroadcast({ patientRecordId })).finally(() => {
                                mismatchRefetchInFlightRef.current = false;
                                // PR review finding: handleActivePatientBroadcast's
                                // setTranscriptSections wholesale-replaces transcriptSections with
                                // its fetch response. If that response raced ahead of server-side
                                // visibility for the section THIS broadcast is about (an event we
                                // know just happened, not a fetch artifact), the optimistic add
                                // below could get silently wiped -- and nothing puts it back, since
                                // a later terminal-status broadcast for an unknown section is
                                // treated as the discard signal, not re-added. Re-assert it once the
                                // refetch settles -- but only when it's genuinely still missing.
                                //
                                // PR review finding: addTranscriptSection MERGE-UPDATES an existing
                                // row (see that reducer), not add-if-missing -- re-dispatching the
                                // ORIGINAL broadcast's payload unconditionally would stomp fresher
                                // status/data/account_id the refetch itself restored, or that a
                                // later status broadcast applied while this refetch was in flight,
                                // with this stale snapshot. Check current state first so this stays
                                // a no-op except in the one race it exists to heal.
                                const stillMissing = !store
                                    .getState()
                                    .activePatientData.transcriptSections.some(
                                        (section) => section.id === transcriptSectionId,
                                    );
                                if (stillMissing) {
                                    dispatch(addTranscriptSection(newSectionPayload));
                                }
                            });
                        }
                    }
                    // Must be addTranscriptSection (not updateOrAddTranscriptSection) — the latter
                    // is update-only despite its name (resurrection guard), so it would no-op here
                    // since we're inside the !sectionExists branch.
                    dispatch(addTranscriptSection(newSectionPayload));
                    // Also seed the visit-list slice. incrementRecordingCount is update-only (no
                    // push, to prevent resurrection), so without an explicit-add the visit-list
                    // row's section count stays stale until the next refetch — and the eventual
                    // terminal completion broadcast would no-op against an empty array.
                    dispatch(
                        addRecordingSection({
                            transcriptionId,
                            sectionId: transcriptSectionId,
                            status,
                        }),
                    );
                    return;
                }

                // `fetchPatientData` dispatches `setTranscriptSections` BEFORE `setPatientData`,
                // so once `isActivePatient` is true, sections are already populated. There is no
                // init-race window where (isActivePatient && sections empty) is a legitimate state;
                // that combo means "user discarded the only section." Treating it as the discard
                // signal here is what closes EN-610 for single-section patients.
                //
                // patient_mismatch = benign navigation (active patient changed since broadcast).
                // section_archived_or_missing = the discard signal (section absent for active patient).
                const reason = isActivePatient ? "section_archived_or_missing" : "patient_mismatch";
                Sentry.addBreadcrumb({
                    category: "realtime",
                    message: `Skipping activePatientData update for transcription event (${reason})`,
                    data: {
                        transcriptSectionId,
                        transcriptionId,
                        patientRecordId,
                        activePatientRecordId: liveActivePatientId,
                        isSuccess,
                        status,
                        reason,
                        transcriptSectionsLength: liveSections.length,
                    },
                    level: isActivePatient ? "info" : "debug",
                });
                return;
            }

            // Realtime never *creates* sections — only updates existing ones. Brand-new sections
            // land via `addTranscriptSection` in upload flows (useFileUpload) or `fetchPatientData`.
            // The guard above guarantees `sectionExists`, so update-only is sufficient.
            if (isSuccess) {
                dispatch(
                    updateOrAddTranscriptSection({
                        transcriptId: transcriptionId,
                        transcriptSection: {
                            id: transcriptSectionId,
                            data: transcript,
                            status: status,
                            audio_duration: audioDuration,
                            unprocessed_filename: unprocessed_filename,
                        },
                    }),
                );
                dispatch(setAssetAsSelected(transcriptSectionId));
            } else {
                dispatch(
                    updateOrAddTranscriptSection({
                        transcriptId: transcriptionId,
                        transcriptSection: {
                            id: transcriptSectionId,
                            data: "",
                            status: TranscriptSectionStatus.FailedErrorProcessing,
                        },
                    }),
                );
            }
        },
        [dispatch, store, channelAccountId],
    );

    const handleDocumentEvent = useCallback(
        (data: any) => {
            applyDocumentUpdate(dispatch, data);
        },
        [dispatch],
    );

    const handleDocumentUpdatedEvent = useCallback(
        (data: any) => {
            const { patientRecordId, chatCompletionId, documentTypeId, section, header, progress, index } = data;

            const tabId = createTabId(documentTypeId, header);
            if (tabId) {
                dispatch(
                    updateDocumentSectionInTab({ tabId, chatId: chatCompletionId, section, index, patientRecordId }),
                );
                dispatch(setProgress({ tabId, progress, patientRecordId }));
            }
        },
        [dispatch],
    );

    const handleDocumentCreatedEvent = useCallback(
        (data: any) => {
            const { patientRecordId, chatCompletion } = data;

            // INSIGHTS documents are never user-facing — drop the realtime event so they
            // don't get injected into the visit's document list (the fetch path also filters them).
            // Fail-closed predicate: the broadcast payload carries `document_types` from whichever
            // client produced it, so it can be null for the same RLS reason a fetched embed can
            // (EN-1071). `document_type_id` is present on this payload — it is read just below.
            if (chatCompletion && isHiddenDocumentForVisitView(chatCompletion)) {
                return;
            }

            dispatch(addDocument({ id: patientRecordId, chatCompletion }));

            const tabId = createTabId(chatCompletion.document_type_id, chatCompletion.chat_json?.header);
            if (tabId) {
                dispatch(addOrUpdateTabAndAddDocument({ tabId, document: chatCompletion, patientRecordId }));
                dispatch(resetProgress({ tabId, patientRecordId }));
                dispatch(addToAllDocuments({ document: chatCompletion, patientRecordId }));
            }

            // update the visit header's updated at time
            dispatch(handleVisitUpdated({ visitId: patientRecordId, updatedAt: new Date().toISOString() }));
        },
        [dispatch],
    );

    const handleDocumentDeletedEvent = useCallback(
        (data: any) => {
            const { chatMessageId, patientRecordId, documentName } = data;

            dispatch(removeDocument({ id: patientRecordId, documentId: chatMessageId }));

            const tabId = createTabId(chatMessageId, documentName);

            if (tabId) {
                dispatch(removeDocumentFromTab({ tabId, documentId: chatMessageId }));
                dispatch(resetProgress({ tabId, patientRecordId }));
                dispatch(removeFromAllDocuments({ documentId: chatMessageId, patientRecordId }));
            }
        },
        [dispatch],
    );

    const handleDocumentSelectionEvent = useCallback(
        (data: any) => {
            const { documentTypeId, remove, doctorId, senderUserId } = data;

            // EN-1051: `documentSelection` is the one peer-to-peer event that carries per-user
            // *intent* rather than shared clinic state. No server emitter exists (see
            // API-Server app/framework/repo/events.ts); it is emitted only by DocumentSelect.tsx and
            // WidgetDocumentSelect.tsx to sync a single human's picks across their own tabs. It was
            // safe only because the channel used to be that human's auth uid. On the clinic-wide
            // channel, another member's clicks would dispatch into this store — gated by nothing but
            // `doctorId`, which routinely matches when a tech records on a DVM's behalf — and
            // `selectedDocuments` decides what gets generated on stop-recording. Silently generating
            // the wrong document set is not acceptable in a medical record product.
            //
            // Restrict delivery to the same human, which reproduces exactly the scope the uid-named
            // channel used to give us. Fail CLOSED when the field is absent (a tab still running a
            // pre-EN-1051 bundle): the cost is losing a redundant cross-tab convenience until that
            // tab reloads, which is strictly preferable to leaking selections between members.
            if (!senderUserId || senderUserId !== session?.user?.id) return;

            // only update if same selected doctor
            const isSameDoctor = doctorId === selectedDoctorRef.current?.id;

            // only update the selected documents if the doctor is the same
            if (remove && isSameDoctor) {
                dispatch(removeSelectedDocument(documentTypeId));
            } else if (!remove && isSameDoctor) {
                dispatch(addSelectedDocument(documentTypeId));
            }
        },
        [dispatch, session?.user?.id],
    );

    const handlePatientDeletedEvent = useCallback(
        (data: any) => {
            const { patientRecordId } = data;
            dispatch(removeRecord({ id: patientRecordId }));
        },
        [dispatch],
    );

    // EN-1325: coalesced per record (and deferred for the board while this tab is hidden) instead of
    // refetching immediately on every broadcast in every open tab.
    const refreshPatientFromBroadcast = usePatientBroadcastRefresh(dispatch);

    const handlePatientEvent = useCallback(
        (data: any) => {
            const { patientRecordId } = data;
            refreshPatientFromBroadcast(patientRecordId);
        },
        [refreshPatientFromBroadcast],
    );

    const handleEmailConfirmationSent = useCallback(
        (data: any) => {
            const { doctorId, sentAt, validateType, communicationId } = data;
            if (!doctorId || !sentAt) return;

            if (validateType === ValidateType.Doctor) {
                // Invalidate doctors query to refetch with updated confirmation_sent_at
                queryClient.invalidateQueries({ queryKey: ["doctors", clinicAccountId] });
            } else if (validateType === ValidateType.Communication) {
                setCommunicationsData((prev) =>
                    prev
                        ? prev.map((comm) =>
                              comm.id === communicationId ? { ...comm, confirmation_sent_at: sentAt } : comm,
                          )
                        : prev,
                );
            }
        },
        [queryClient, setCommunicationsData, clinicAccountId],
    );

    const handleEmailConfirmed = useCallback(
        (data: any) => {
            const { doctorId, confirmedAt, validateType, communicationId } = data;
            if (!doctorId || !confirmedAt) return;

            if (validateType === ValidateType.Doctor) {
                // Invalidate doctors query to refetch with updated email_confirmed_at
                queryClient.invalidateQueries({ queryKey: ["doctors", clinicAccountId] });
            } else if (validateType === ValidateType.Communication) {
                setCommunicationsData((prev) =>
                    prev
                        ? prev.map((comm) =>
                              comm.id === communicationId ? { ...comm, confirmed_at: confirmedAt } : comm,
                          )
                        : prev,
                );
            }
        },
        [queryClient, setCommunicationsData, clinicAccountId],
    );

    const handlePhoneNumberVerificationStatusUpdated = useCallback(
        (data: { phoneNumberId: string; phoneNumber: string; status: string }) => {
            const { phoneNumberId, phoneNumber, status } = data;
            queryClient.setQueryData([...PHONE_NUMBER_VERIFICATION_STATUS_QUERY_KEY, phoneNumberId], {
                phoneNumber,
                status,
            });
        },
        [queryClient],
    );

    const handleCallStatusUpdated = useCallback(
        (data: { callSid: string; callStatus: string; patientRecordId: string; timestamp: string }) => {
            queryClient.setQueryData([...CALL_STATUS_QUERY_KEY, data.callSid], data);
        },
        [queryClient],
    );

    /*
     * The one broadcast dispatcher, shared by every channel this provider holds.
     *
     * EN-1051: there are now up to two channels (the clinic account id and, when it differs, the
     * auth uid — see `userChannelId`), and they must behave identically. Defined once here rather
     * than inline per channel so the two can never drift apart.
     */
    const handleBroadcastMessage = useCallback(
        // The supabase broadcast payload is untyped; `data` is per-event and narrowed by each handler.
        (message: { payload: any }) => {
            const { type, eventType, data } = message.payload;
            const isSuccess = type === BroadcastTypes.success;

            switch (eventType) {
                case BroadcastEvents.transcription:
                    handleTranscriptionEvent(isSuccess, data);
                    break;
                case BroadcastEvents.document:
                    handleDocumentEvent(data);
                    break;
                case BroadcastEvents.documentUpdated:
                    handleDocumentUpdatedEvent(data);
                    break;
                case BroadcastEvents.documentCreated:
                    handleDocumentCreatedEvent(data);
                    break;
                case BroadcastEvents.documentDeleted:
                    handleDocumentDeletedEvent(data);
                    break;
                case BroadcastEvents.documentSelection:
                    handleDocumentSelectionEvent(data);
                    break;
                case BroadcastEvents.patientDeleted:
                    handlePatientDeletedEvent(data);
                    break;
                case BroadcastEvents.patientEvent:
                    handlePatientEvent(data);
                    break;
                case BroadcastEvents.emailConfirmationSent:
                    handleEmailConfirmationSent(data);
                    break;
                case BroadcastEvents.emailConfirmed:
                    handleEmailConfirmed(data);
                    break;
                case BroadcastEvents.phoneNumberVerificationStatusUpdated:
                    handlePhoneNumberVerificationStatusUpdated(data);
                    break;
                case BroadcastEvents.callStatusUpdated:
                    handleCallStatusUpdated(data);
                    break;
                default:
                    break;
            }
        },
        [
            handleTranscriptionEvent,
            handleDocumentEvent,
            handleDocumentUpdatedEvent,
            handleDocumentCreatedEvent,
            handleDocumentDeletedEvent,
            handleDocumentSelectionEvent,
            handlePatientDeletedEvent,
            handlePatientEvent,
            handleEmailConfirmationSent,
            handleEmailConfirmed,
            handlePhoneNumberVerificationStatusUpdated,
            handleCallStatusUpdated,
        ],
    );

    /*
     * This function safely subscribes to the Realtime channel(s)
     * It removes the previous channel(s) and then creates fresh ones
     */
    const subscribeToChannel = useCallback(async () => {
        // EN-1051: the clinic channel can no longer be created before the clinic account resolves —
        // its name IS the resolved clinic id. Both effects below re-run once it lands. The uid
        // channel is created in the same pass rather than earlier: keeping one setup path means one
        // teardown path, and the window before `/account/resolved` lands is the same short
        // post-login window the clinic channel already waits out.
        if (!channelAccountId) return;

        // Store old channels for cleanup
        const oldChannel = supaSuccessChannelRef.current;
        const oldUserChannel = userChannelRef.current;

        // CRITICAL: Remove old channel BEFORE creating a new one
        // Supabase caches channels by name, so if an errored channel exists,
        // calling supabase.channel(channelAccountId) returns the SAME errored channel object.
        // This causes:
        // 1. oldChannel === channel (same object), so removal check fails
        // 2. channel.state === "errored" (not "closed"), so subscribe() never called
        // Result: realtime connection permanently broken
        if (oldChannel) {
            try {
                await supabase.removeChannel(oldChannel);
            } catch (e) {
                Sentry.captureException(e);
            }
            // Clear the ref after removal to ensure we get a fresh channel
            supaSuccessChannelRef.current = null;
        }
        if (oldUserChannel) {
            try {
                await supabase.removeChannel(oldUserChannel);
            } catch (e) {
                Sentry.captureException(e);
            }
            userChannelRef.current = null;
        }

        // Now create a fresh channel (guaranteed to be new since we removed the old one)
        const channel = supabase
            .channel(channelAccountId)
            .on("broadcast", { event: BROADCAST_EVENT }, handleBroadcastMessage);

        // Set channel ref so emitEvent can use it (httpSend works via REST)
        supaSuccessChannelRef.current = channel;

        // Subscribe to the channel (should be in "closed" state after creation)
        if (channel.state === "closed") {
            channel.subscribe(handleSubscriptionChange);
        } else {
            Sentry.addBreadcrumb({
                message: "Channel not closed after creation, skipping subscribe",
                level: "warning",
                data: {
                    userId: session?.user?.id,
                    channelAccountId,
                    channelState: channel.state,
                },
            });
        }

        // EN-1051: the uid-named companion channel that keeps Transcribe's transcript-section
        // broadcasts arriving. Same dispatcher, so anything that lands here is handled exactly as it
        // would be on the clinic channel.
        //
        // It shares `handleSubscriptionChange` with the clinic channel. An earlier revision gave it
        // a log-only callback, reasoning that both channels ride one websocket so a transport
        // failure surfaces on the clinic channel and its reconnect restores this one too. That
        // covers transport failure but NOT a channel-specific one — a per-topic join rejection or
        // rate limit can kill this channel while the clinic channel stays healthy, and since
        // Transcribe's transcript-section status arrives only here with no polling fallback, the
        // result was a silent stall until a clinic switch or remount rebuilt both.
        //
        // Sharing the callback is deliberate rather than giving this channel its own budget: one
        // websocket deserves one reconnect budget, and `isRetryingRef` already collapses concurrent
        // failures on both channels into a single rebuild instead of two competing ones.
        if (userChannelId) {
            const userChannel = supabase
                .channel(userChannelId)
                .on("broadcast", { event: BROADCAST_EVENT }, handleBroadcastMessage);
            userChannelRef.current = userChannel;
            if (userChannel.state === "closed") {
                userChannel.subscribe((status) => {
                    if (status !== "SUBSCRIBED") {
                        // Named separately from the clinic channel's own breadcrumb so Sentry can
                        // tell which of the two failed.
                        Sentry.addBreadcrumb({
                            category: "realtime",
                            message: `${status} on the uid companion channel`,
                            level: "warning",
                            data: { userId: session?.user?.id },
                        });
                    }
                    handleSubscriptionChange(status);
                });
            } else {
                Sentry.addBreadcrumb({
                    message: "Uid companion channel not closed after creation, skipping subscribe",
                    level: "warning",
                    data: { userId: session?.user?.id, channelState: userChannel.state },
                });
            }
        }
    }, [
        channelAccountId,
        userChannelId,
        session?.user?.id,
        handleSubscriptionChange,
        handleBroadcastMessage,
        supabase,
    ]);

    // Update the ref synchronously whenever subscribeToChannel changes
    // This ensures the ref is always current before any other effects run
    // useLayoutEffect runs synchronously after DOM mutations but before paint,
    // ensuring it runs before regular useEffect hooks
    useLayoutEffect(() => {
        subscribeToChannelRef.current = subscribeToChannel;
    }, [subscribeToChannel]);

    // Trigger channel setup when subscribeToChannel becomes available or user changes
    // This runs after the ref is updated (useLayoutEffect runs first)
    useEffect(() => {
        // If we have all the prerequisites, set up the channel immediately
        // Only if no channel exists or it's in a bad state (prevents double calls)
        if (supabase && channelAccountId && !isLoading && !isSettingUpChannelRef.current) {
            const channel = supaSuccessChannelRef.current;
            // Check the channel belongs to the currently-resolved clinic by comparing with the ref
            const isChannelForCurrentAccount = channel && currentChannelIdRef.current === channelAccountId;

            if (!channel || !isChannelForCurrentAccount || channel.state === "closed" || channel.state === "errored") {
                isSettingUpChannelRef.current = true;
                // The ref is updated synchronously via useLayoutEffect before this effect runs,
                // so it should always have the latest function with the current clinic account id
                subscribeToChannelRef.current?.().finally(() => {
                    isSettingUpChannelRef.current = false;
                });
            }
        }
    }, [subscribeToChannel, supabase, channelAccountId, userChannelId, isLoading]);

    const value = {
        selectedDoctorRef,
        selectedDocumentsRef,
        generateOnStopRef,
        activeMonitoringDocumentIdRef,
        setActiveMonitoringDocumentId,
        recordingMonitoringDocumentIdRef,
        captureMonitoringDocumentIdForRecording,
        emitEvent,
    };

    return <RealTimeContextApi.Provider value={value}>{children}</RealTimeContextApi.Provider>;
};

export const useRealtimeContextApi = () => {
    const context = useContext(RealTimeContextApi);
    if (context === undefined) {
        throw new Error(`useRealtimeContextApi must be used within a RealTimeContextProvider`);
    }
    return context;
};
